import { useMemo } from "react";
import {
  ChartCard,
  ChartEmptyState,
  ChartErrorState,
  ChartLoading,
  LineAreaChart,
  type LineAreaChartConfig,
} from "@/shared/charts";
import { PeopleIcon } from "@/shared/ui/console-icons";
import type { ECharts } from "echarts";
import type { DensityTrendPoint } from "@/features/deployment-dashboard/widgets/charts/density-trend/types";
import {
  getDensityTrendErrorMessage,
  mapDensityTrendToLineAreaChartData,
} from "@/features/deployment-dashboard/widgets/charts/density-trend/density-trend-chart.utils";

interface DensityTrendChartProps {
  data: DensityTrendPoint[];
  xAxisRange?: { min: number; max: number };
  loading?: boolean;
  error?: Error | string | null;
  onChartReady?: (instance: ECharts) => void;
}

const DENSITY_TREND_CHART_CONFIG: LineAreaChartConfig = {
  seriesLabel: "Average Density",
  ariaDescription: "Density trend chart showing average people per square metre over time",
};

export function DensityTrendChart({
  data,
  xAxisRange,
  loading = false,
  error = null,
  onChartReady,
}: DensityTrendChartProps) {
  const chartData = useMemo(() => mapDensityTrendToLineAreaChartData(data), [data]);
  const chartConfig = useMemo<LineAreaChartConfig>(
    () => ({ ...DENSITY_TREND_CHART_CONFIG, xAxisRange }),
    [xAxisRange]
  );
  const errorMessage = getDensityTrendErrorMessage(error);

  return (
    <ChartCard
      title="Density Trend"
      icon={<PeopleIcon sx={{ fontSize: "0.875rem" }} />}
      footerNote="Tracks average occupants per square metre, derived from population and area size."
    >
      {loading ? (
        <ChartLoading label="Loading density trend data" />
      ) : errorMessage != null ? (
        <ChartErrorState message={errorMessage} />
      ) : data.length === 0 || data.every((point) => point.density == null) ? (
        <ChartEmptyState
          title="No density trend data"
          description="Density trend data will appear once observations are available."
        />
      ) : (
        <LineAreaChart
          data={chartData}
          config={chartConfig}
          height="100%"
          testId="density-trend-chart"
          onChartReady={onChartReady}
        />
      )}
    </ChartCard>
  );
}
